import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LogoutIcon from "@mui/icons-material/Logout";
import { DateTime } from "luxon";

import { formatDuration } from "../dashboardHelpers";
import { holdUntilLabel } from "../../ClassesView/components/WaitlistAction";
import RowShell from "./RowShell";

const ACTION_ORDER = { order: -1 };
const ACTION_SX = { ...ACTION_ORDER, height: "1.75rem", px: 1.5, textTransform: "none", boxShadow: "none" };

// What the client is to this list. A rank is only shown where the club hands
// seats out in turn; first-to-book lists promise none.
const standing = (item) => {
  if (item.started) return { label: "ENDED", color: "default" };
  if (item.closed) return { label: "CLOSED", color: "default" };
  if (item.offer?.expiresAt) return { label: "YOUR TURN", color: "success" };
  if (item.mode === "ordered" && item.position) return { label: `#${item.position} IN LINE`, color: "primary" };
  return { label: "ON WAITLIST", color: "primary" };
};

// One class session the client is waiting for. A live hold means the seat is
// theirs to book until it lapses, so Book now leads the row; otherwise they can
// look at the class or step off the list. Once the list has closed (cutoff
// passed or the session happened) there is nothing left to wait for or leave.
function WaitlistRow({ item, onOpen, onLeave, leaving }) {
  // startDate is fake UTC (club wall time labelled Z) — read it in the UTC frame.
  const dt = item.startDate ? DateTime.fromISO(item.startDate, { zone: "utc" }) : null;
  const durationLabel = formatDuration(item.duration);
  const status = standing(item);
  const isClosed = Boolean(item.closed || item.started);
  const hasOffer = !isClosed && Boolean(item.offer?.expiresAt);

  return (
    <RowShell stack>
      <Box flex={1} minWidth={0}>
        <Typography variant="subtitle2" noWrap>
          {item.name || "Class"}
        </Typography>
        {dt?.isValid && (
          <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 600 }}>
            {dt.toFormat("yyyy-MM-dd")} at {dt.toFormat("HH:mm")}
            {durationLabel && ` • ${durationLabel}`}
            {item.locationName && ` — ${item.locationName}`}
          </Typography>
        )}
        {(item.trainerName || item.facilityName) && (
          <Typography variant="body2" color="textSecondary">
            {[item.trainerName, item.facilityName].filter(Boolean).join(" • ")}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          flexShrink: 0,
          justifyContent: { xs: "flex-start", sm: "flex-end" }
        }}
      >
        <Chip
          size="small"
          label={status.label}
          color={status.color}
          variant="outlined"
          sx={{ height: "1.375rem", fontSize: "0.7rem", fontWeight: 500, letterSpacing: 0.3 }}
        />
        {hasOffer && (
          <Box display="flex" alignItems="center" gap={0.5} sx={{ color: "warning.main" }}>
            <AccessTimeIcon sx={{ fontSize: "1rem" }} />
            <Typography variant="caption" fontWeight={600}>
              Seat held until {holdUntilLabel(item.offer.expiresAt)}
            </Typography>
          </Box>
        )}
        {hasOffer ? (
          <Button size="small" variant="contained" onClick={() => onOpen?.(item)} sx={{ ...ACTION_SX, color: "common.white" }}>
            Book now
          </Button>
        ) : (
          <Button size="small" variant="outlined" onClick={() => onOpen?.(item)} sx={ACTION_SX}>
            View class
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={leaving || isClosed}
          startIcon={leaving ? null : <LogoutIcon sx={{ fontSize: "0.875rem" }} />}
          onClick={() => onLeave?.(item)}
          sx={{
            ...ACTION_ORDER,
            height: "1.75rem",
            minWidth: "auto",
            px: 1.25,
            borderRadius: "0.375rem",
            fontSize: "0.75rem",
            fontWeight: 500,
            textTransform: "none",
            "& .MuiButton-startIcon": { ml: 0, mr: 0.5 }
          }}
        >
          {leaving ? <CircularProgress size={14} color="inherit" /> : "Leave"}
        </Button>
      </Box>
    </RowShell>
  );
}

export default WaitlistRow;
